import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Sun, Moon } from "lucide-react";
import { CohnectLogo } from "./CohnectLogo";
import { usePublicTheme } from "@/contexts/ThemeContext";

const DISCORD_INVITE_URL = "https://discord.gg/cohnect";

const linkBase =
  "text-sm font-semibold text-[color:var(--coh-ink)] underline-offset-4 hover:underline hover:decoration-2 hover:text-[color:var(--coh-blue)] transition-colors";
const linkActive = "underline decoration-2 text-[color:var(--coh-blue)]";

const ThemeToggle = ({ className = "" }) => {
  const { theme, toggleTheme } = usePublicTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={`inline-flex h-10 w-10 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] text-[color:var(--coh-ink)] shadow-nb-sm transition-[transform,box-shadow] duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-nb active:translate-x-0.5 active:translate-y-0.5 ${className}`}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
};

export const SiteHeader = () => {
  const { theme } = usePublicTheme();
  const [open, setOpen] = React.useState(false);
  const logoTone = theme === "dark" ? "light" : "ink";
  return (
    <header data-testid="site-header" className="sticky top-0 z-40 border-b-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)]">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <Link to="/" data-testid="site-header-logo" className="flex items-center">
          <CohnectLogo tone={logoTone} height={26} />
        </Link>
        <nav data-testid="site-header-nav" className="hidden md:flex items-center gap-7">
          <NavLink to="/issues" data-testid="site-header-nav-issues" className={({isActive}) => `${linkBase} ${isActive ? linkActive : ''}`}>Issues</NavLink>
          <NavLink to="/about" data-testid="site-header-nav-about" className={({isActive}) => `${linkBase} ${isActive ? linkActive : ''}`}>About</NavLink>
          <NavLink to="/support" data-testid="site-header-nav-support" className={({isActive}) => `${linkBase} ${isActive ? linkActive : ''}`}>Support</NavLink>
          <NavLink to="/contact" data-testid="site-header-nav-contact" className={({isActive}) => `${linkBase} ${isActive ? linkActive : ''}`}>Contact</NavLink>
          <ThemeToggle />
          <Button asChild size="sm" data-testid="site-header-join-discord-button" className="btn-nb btn-nb-primary h-10 px-4 text-sm">
            <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">Join Discord <span aria-hidden="true" className="font-mono ml-1">↗</span></a>
          </Button>
        </nav>
        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button className="inline-flex h-10 w-10 items-center justify-center border-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)] text-[color:var(--coh-ink)] shadow-nb-sm transition-[transform,box-shadow] duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-nb active:translate-x-0.5 active:translate-y-0.5" aria-label="Menu" data-testid="site-header-mobile-menu-button" onClick={() => setOpen(v => !v)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t-2 border-[color:var(--coh-ink)] bg-[color:var(--coh-paper-2)]">
          <div className="px-4 py-4 flex flex-col gap-1 text-sm text-[color:var(--coh-ink)]">
            <Link to="/issues" onClick={() => setOpen(false)} className="min-h-[44px] flex items-center font-semibold underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Issues</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="min-h-[44px] flex items-center font-semibold underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">About</Link>
            <Link to="/support" onClick={() => setOpen(false)} className="min-h-[44px] flex items-center font-semibold underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Support</Link>
            <Link to="/contact" onClick={() => setOpen(false)} className="min-h-[44px] flex items-center font-semibold underline-offset-4 hover:underline hover:text-[color:var(--coh-blue)]">Contact</Link>
            <a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="btn-nb btn-nb-primary mt-2 inline-flex h-11 items-center justify-center px-4 font-semibold">Join Discord <span aria-hidden="true" className="font-mono ml-1">↗</span></a>
          </div>
        </div>
      )}
    </header>
  );
};
